import { parseChallenge } from '@geosport/shared';
import { useEffect } from 'react';
import { ConsentBanner } from './components/ConsentBanner';
import { track } from './lib/analytics';
import { rememberChallenge } from './lib/challenge';
import { gaPageView } from './lib/ga';
import { applyRouteMeta } from './lib/meta';
import { usePath } from './lib/router';
import { Admin } from './screens/Admin';
import { GroupDetail } from './screens/GroupDetail';
import { Groups } from './screens/Groups';
import { Home } from './screens/Home';
import { HowItWorks } from './screens/HowItWorks';
import { Join } from './screens/Join';
import { Leaderboard } from './screens/Leaderboard';
import { NotFound } from './screens/NotFound';
import { Play } from './screens/Play';
import { Practice } from './screens/Practice';
import { Privacy } from './screens/Privacy';
import { Results } from './screens/Results';
import { Stats } from './screens/Stats';
import { Terms } from './screens/Terms';
import { useRound } from './store/round';

export function App() {
  const path = usePath();

  useEffect(() => {
    void useRound.getState().load();
  }, []);

  // Title, description and canonical for this route, then the analytics page view.
  useEffect(() => {
    applyRouteMeta(path);
    gaPageView();
  }, [path]);

  // Arriving from a friend's "beat my score" link: remember it for the results screen.
  useEffect(() => {
    const challenge = parseChallenge(path);
    if (!challenge) return;
    rememberChallenge(challenge);
    track('challenge_opened', { puzzleNo: challenge.puzzleNo, score: challenge.score });
  }, [path]);

  return (
    <>
      <Screen path={path} />
      <ConsentBanner path={path} />
    </>
  );
}

function Screen({ path }: { path: string }) {
  const group = /^\/groups\/([\w-]+)$/.exec(path);
  if (group) return <GroupDetail id={group[1]!} />;
  const join = /^\/join\/([A-Za-z0-9]+)$/.exec(path);
  if (join) return <Join code={join[1]!} />;
  if (path.startsWith('/play')) return <Play />;
  const challenge = parseChallenge(path);
  if (challenge) return <Home challenge={challenge} />;

  switch (path) {
    case '/':
      return <Home />;
    case '/results':
      return <Results />;
    case '/leaderboard':
      return <Leaderboard />;
    case '/stats':
      return <Stats />;
    case '/groups':
      return <Groups />;
    case '/practice':
    case '/archive':
      return <Practice />;
    case '/how-to-play':
    case '/how-it-works':
      return <HowItWorks />;
    case '/admin':
      return <Admin />;
    case '/privacy':
      return <Privacy />;
    case '/terms':
      return <Terms />;
    default:
      return <NotFound />;
  }
}
